/**
 * Market List Component
 * 
 * Displays all available markets for users to browse and select.
 * Toggle between Markets and Positions views.
 * Uses client-side pagination, search, and filtering.
 */

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMarket, EnrichedMarket, OnChainMarketState, OnChainOptionState } from '../../contexts/MarketProvider';
import { Market } from '../../types/market';
import { marketApiService } from '../../services/marketApiService';
import { MarketListItem } from './MarketListItem';
import { PositionsList } from '../PositionsList/PositionsList';
import './MarketList.css';

type ViewType = 'markets' | 'positions';
type MarketTab = 'trending' | 'ending' | 'resolved';

// Number of markets to display initially and per "Load More" click
const DISPLAY_INCREMENT = 12;

export const MarketList: React.FC = () => {
  const navigate = useNavigate();
  const { 
    markets, 
    isLoadingMarkets, 
    loadMarkets,
    userPositions,
    isLoadingPositions,
    refreshUserPositions,
  } = useMarket();
  
  const [activeView, setActiveView] = useState<ViewType>('markets');
  
  // Client-side display pagination - how many markets to show
  const [displayCount, setDisplayCount] = useState(DISPLAY_INCREMENT);
  
  // Search and sort state (for Markets view only)
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<MarketTab>('trending');
  
  // Resolved (closed) markets - separate state since they come from a different API call
  const [resolvedMarkets, setResolvedMarkets] = useState<EnrichedMarket[]>([]);
  const [isLoadingResolved, setIsLoadingResolved] = useState(false);
  const [resolvedLoaded, setResolvedLoaded] = useState(false);
  
  // Load markets on mount (lazy loading - only when MarketList is visited)
  useEffect(() => {
    // Only load if markets haven't been loaded yet
    if (markets.length === 0 && !isLoadingMarkets) {
      loadMarkets(1, 'active');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount
  
  /**
   * Convert a Market with outcome data to an EnrichedMarket.
   * Closed markets have outcome snapshots, so no RPC call needed.
   */
  const enrichClosedMarket = useCallback((market: Market): EnrichedMarket | null => {
    const outcome = market.outcome;
    if (!outcome || !outcome.options_snapshot) return null;
    
    const options: OnChainOptionState[] = outcome.options_snapshot.map((opt) => ({
      optionIndex: opt.optionIndex,
      totalDeposits: opt.totalDeposits,
      totalTokens: opt.totalTokens,
      virtualLiquidity: 0,
      percentage: opt.percentage,
      isLeading: opt.isWinner
    }));
    
    const onChainState: OnChainMarketState = {
      marketId: 0,
      creator: market.creator,
      startTime: Math.floor(new Date(market.starts_at).getTime() / 1000),
      endTime: Math.floor(new Date(market.ends_at).getTime() / 1000),
      numOptions: options.length,
      totalDeposits: outcome.total_volume_lamports,
      totalParticipants: outcome.total_participants,
      options,
      resolved: outcome.resolved,
      winner: outcome.winning_option_index
    };
    
    return {
      ...market,
      onChainState,
      outcome: market.outcome,
      userPosition: undefined
    };
  }, []);
  
  // Load resolved markets directly from API (separate from shared markets state)
  const loadResolvedMarkets = useCallback(async () => {
    setIsLoadingResolved(true);
    try {
      const result = await marketApiService.getMarkets({
        page: 1,
        limit: 500,
        status: 'closed'
      });
      
      if (!result.success || !result.data) {
        setResolvedMarkets([]);
        setResolvedLoaded(true);
        return;
      }
      
      // Enrich closed markets using their outcome snapshots
      const enriched: EnrichedMarket[] = [];
      for (const market of result.data) {
        const enrichedMarket = enrichClosedMarket(market);
        if (enrichedMarket) {
          enriched.push(enrichedMarket);
        }
      }
      
      setResolvedMarkets(enriched);
      setResolvedLoaded(true);
    } catch (err) {
      console.error('Failed to load resolved markets:', err);
    } finally {
      setIsLoadingResolved(false);
    }
  }, [enrichClosedMarket]);
  
  // Count positions that can be sold (winners)
  const sellableCount = userPositions.filter(p => p.canClaim).length;
  
  // Filter and sort markets based on search query and active tab
  const filteredAndSortedMarkets = useMemo(() => {
    // For resolved tab, use the separate resolved markets list
    if (activeTab === 'resolved') {
      let filtered = [...resolvedMarkets];
      
      // Filter by search query
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase().trim();
        filtered = filtered.filter(market => 
          market.question.toLowerCase().includes(query) ||
          market.options.some(opt => opt.toLowerCase().includes(query))
        );
      }
      
      // Sort by most recently ended first
      return filtered.sort((a, b) => {
        const endA = new Date(a.ends_at).getTime();
        const endB = new Date(b.ends_at).getTime();
        return endB - endA;
      });
    }
    
    const now = Date.now();
    
    // 1. Only show active (non-ended) markets for trending/ending tabs
    let filtered = markets.filter(market => {
      const isEnded = new Date(market.ends_at).getTime() <= now;
      return !isEnded;
    });
    
    // 2. Filter by search query (case-insensitive)
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(market => 
        market.question.toLowerCase().includes(query) ||
        market.options.some(opt => opt.toLowerCase().includes(query))
      );
    }
    
    // 3. Sort based on selected tab
    if (activeTab === 'trending') {
      // Sort by volume (highest first)
      return filtered.sort((a, b) => {
        const volumeA = a.onChainState?.totalDeposits ?? 0;
        const volumeB = b.onChainState?.totalDeposits ?? 0;
        return volumeB - volumeA;
      });
    } else {
      // Sort by ending soon (soonest first)
      return filtered.sort((a, b) => {
        const endA = new Date(a.ends_at).getTime();
        const endB = new Date(b.ends_at).getTime();
        return endA - endB;
      });
    }
  }, [markets, resolvedMarkets, activeTab, searchQuery]);
  
  // Client-side pagination: only show first N markets
  const displayedMarkets = filteredAndSortedMarkets.slice(0, displayCount);
  const hasMoreToDisplay = displayCount < filteredAndSortedMarkets.length;
  
  // Handle view toggle - refresh positions when switching to positions view
  const handleViewChange = useCallback((view: ViewType) => {
    setActiveView(view);
    if (view === 'positions') {
      refreshUserPositions();
    }
  }, [refreshUserPositions]);
  
  const handleSelectMarket = (address: string) => {
    navigate(`/market/${address}`);
  };
  
  const handleRefresh = () => {
    if (activeView === 'markets') {
      setDisplayCount(DISPLAY_INCREMENT); // Reset display count
      setSearchQuery(''); // Clear search
      if (activeTab === 'resolved') {
        loadResolvedMarkets();
      } else {
        loadMarkets(1, 'active'); // Load active markets for trending/ending
      }
    } else {
      refreshUserPositions();
    }
  };
  
  // Client-side "Load More" - just show more from already-loaded markets (instant, no API call)
  const handleLoadMore = () => {
    setDisplayCount(prev => prev + DISPLAY_INCREMENT);
  };
  
  // Handle tab change
  const handleTabChange = (tab: MarketTab) => {
    setActiveTab(tab);
    setDisplayCount(DISPLAY_INCREMENT); // Reset display count when tab changes
    
    if (tab === 'resolved') {
      // Load resolved markets if not already loaded
      if (!resolvedLoaded) {
        loadResolvedMarkets();
      }
    }
  };
  
  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setDisplayCount(DISPLAY_INCREMENT); // Reset display count when search changes
  };
  
  // Navigate to market when clicking on a position
  const handleViewOpinion = (marketId: number) => {
    const position = userPositions.find(p => p.market.id === marketId);
    if (position) {
      navigate(`/market/${position.market.address}`);
    }
  };
  
  // Determine if we're in a loading state
  const isCurrentlyLoading = activeTab === 'resolved' ? isLoadingResolved : isLoadingMarkets;
  
  // Render header with toggle
  const renderHeader = () => (
    <div className="market-list-header">
      <h3 className="market-list-toggle">
        <span 
          className={`toggle-option ${activeView === 'markets' ? 'active' : ''}`}
          onClick={() => handleViewChange('markets')}
        >
          Markets
        </span>
        <span className="toggle-separator">|</span>
        <span 
          className={`toggle-option ${activeView === 'positions' ? 'active' : ''}`}
          onClick={() => handleViewChange('positions')}
        >
          Positions
          {sellableCount > 0 && <span className="toggle-badge">({sellableCount})</span>}
        </span>
      </h3>
      <div className="market-list-header-right">
        <button 
          className="market-list-refresh-icon" 
          onClick={handleRefresh}
          disabled={activeView === 'markets' ? isCurrentlyLoading : isLoadingPositions}
          title="Refresh"
        >
          {(activeView === 'markets' ? isCurrentlyLoading : isLoadingPositions) ? '...' : '↻'}
        </button>
      </div>
    </div>
  );
  
  // Render search and filter bar (only for Markets view)
  const renderFilterBar = () => (
    <div className="market-list-filter-bar">
      <div className="market-list-search">
        <span className="search-icon">🔍</span>
        <input
          type="text"
          placeholder="Search..."
          value={searchQuery}
          onChange={handleSearchChange}
          className="search-input"
        />
        {searchQuery && (
          <button 
            className="search-clear" 
            onClick={() => {
              setSearchQuery('');
              setDisplayCount(DISPLAY_INCREMENT);
            }}
          >
            ✕
          </button>
        )}
      </div>
      <div className="market-list-status-tabs">
        <button
          className={`status-tab ${activeTab === 'trending' ? 'active' : ''}`}
          onClick={() => handleTabChange('trending')}
        >
          Trending
        </button>
        <button
          className={`status-tab ${activeTab === 'ending' ? 'active' : ''}`}
          onClick={() => handleTabChange('ending')}
        >
          Ending
        </button>
        <button
          className={`status-tab ${activeTab === 'resolved' ? 'active' : ''}`}
          onClick={() => handleTabChange('resolved')}
        >
          Resolved
        </button>
      </div>
    </div>
  );

  // Loading state for markets view
  if (activeView === 'markets' && isCurrentlyLoading && (activeTab === 'resolved' ? resolvedMarkets.length === 0 : markets.length === 0)) {
    return (
      <div className="market-list-container">
        {renderHeader()}
        {renderFilterBar()}
        <div className="market-list-loading">
          <div className="market-list-spinner" />
          <p>{activeTab === 'resolved' ? 'Loading resolved markets...' : 'Loading markets...'}</p>
        </div>
      </div>
    );
  }
  
  // Empty state for markets view (only when no data at all and not on resolved tab with unloaded data)
  if (activeView === 'markets' && activeTab !== 'resolved' && markets.length === 0) {
    return (
      <div className="market-list-container">
        {renderHeader()}
        <div className="market-list-empty">
          <p>No markets available</p>
          <span>Be the first to create a market!</span>
          <button className="market-list-refresh-button" onClick={handleRefresh}>
            Refresh
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="market-list-container">
      {renderHeader()}
      
      {activeView === 'markets' ? (
        <>
          {/* Search and Filter Bar */}
          {renderFilterBar()}
          
          {/* Empty state for filtered results */}
          {filteredAndSortedMarkets.length === 0 && !isCurrentlyLoading ? (
            <div className="market-list-empty">
              <p>{activeTab === 'resolved' ? 'No resolved markets yet' : 'No active markets found'}</p>
              <span>{searchQuery ? `No results for "${searchQuery}"` : activeTab === 'resolved' ? 'Markets will appear here once they end' : 'Check back later!'}</span>
            </div>
          ) : (
            <>
              {/* Market grid */}
              <div className="market-list-grid">
                {displayedMarkets.map((market) => (
                  <MarketListItem
                    key={market.address}
                    market={market}
                    onClick={() => handleSelectMarket(market.address)}
                  />
                ))}
              </div>
              
              {/* Load More Button - client-side pagination (instant, no API call) */}
              {hasMoreToDisplay && (
                <div className="market-list-load-more">
                  <button
                    className="load-more-button"
                    onClick={handleLoadMore}
                  >
                    Load More ({displayedMarkets.length} of {filteredAndSortedMarkets.length})
                  </button>
                </div>
              )}
            </>
          )}
        </>
      ) : (
        <PositionsList
          positions={userPositions}
          isLoading={isLoadingPositions}
          onViewOpinion={handleViewOpinion}
        />
      )}
    </div>
  );
};

export default MarketList;
